import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import {
  addClip,
  barPosToBeats,
  beatsPerBar,
  beatsToBarPos,
  clipSpan,
  docLengthSeconds,
  layerLength,
  moveClip,
  resizeClip,
  scaleEnvelope,
  secondsPerBeat,
  snapTo,
  tempoOf,
  timeSignatureOf,
  updateLayer,
  updateVoiceLine,
  addLayer,
  addVoiceLine,
  patternLength,
  voiceLineLength,
  voiceLineStarts,
  type SoundDoc,
} from '../../lib/sound-doc.js';
import { soundPlayer, usePlayback } from '../../lib/sound-player.js';
import { useSound, type SoundSelection } from '../../state/sound.js';
import { Icon } from '../ui/icons.js';

/**
 * Arrangement view. One time axis in seconds: songs draw bars/beats from the
 * tempo and snap clips to them; sound effects show layers as blocks on a
 * seconds ruler; voice lines get their own row. Drag to move, drag the right
 * edge to resize, Alt+drag to copy, double-click a clip to edit its pattern,
 * click the ruler to seek and Shift+drag it to set the loop region.
 */

const HEADER_W = 168;
const RULER_H = 26;
const TRACK_H = 46;
const LAYER_H = 34;

type RowKind = 'track' | 'layer' | 'voice';
interface Row {
  kind: RowKind;
  index: number;
  y: number;
  h: number;
}

type Drag =
  | { kind: 'clip'; track: number; clip: number; x0: number; y0: number; dx: number; dy: number; copy: boolean }
  | { kind: 'clipResize'; track: number; clip: number; x0: number; dx: number }
  | { kind: 'layer'; index: number; x0: number; dx: number; copy: boolean }
  | { kind: 'layerResize'; index: number; x0: number; dx: number }
  | { kind: 'line'; index: number; x0: number; dx: number }
  | { kind: 'loop'; t0: number; t1: number }
  | { kind: 'scrub' };

export type SnapMode = 'bar' | 'beat' | 'sixteenth' | 'off' | '100ms' | '10ms';

function rowsOf(doc: SoundDoc): Row[] {
  const rows: Row[] = [];
  let y = 0;
  (doc.tracks ?? []).forEach((_, index) => {
    rows.push({ kind: 'track', index, y, h: TRACK_H });
    y += TRACK_H;
  });
  (doc.layers ?? []).forEach((_, index) => {
    rows.push({ kind: 'layer', index, y, h: LAYER_H });
    y += LAYER_H;
  });
  if (doc.voice?.lines?.length) rows.push({ kind: 'voice', index: 0, y, h: LAYER_H });
  return rows;
}

function tickStep(pxPerSecond: number): number {
  for (const s of [0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60]) if (s * pxPerSecond >= 64) return s;
  return 120;
}

function fmtTick(t: number, step: number): string {
  if (step < 0.01) return `${(t * 1000).toFixed(0)}ms`;
  if (step < 1) return `${t.toFixed(step < 0.1 ? 2 : 1)}s`;
  if (t >= 60) return `${Math.floor(t / 60)}:${String(Math.round(t % 60)).padStart(2, '0')}`;
  return `${Math.round(t)}s`;
}

function isSel(sel: SoundSelection | null, want: SoundSelection): boolean {
  return !!sel && JSON.stringify(sel) === JSON.stringify(want);
}

export function Timeline() {
  const doc = useSound((s) => s.doc);
  const selection = useSound((s) => s.selection);
  const solo = useSound((s) => s.solo);
  const audio = useSound((s) => s.audio);
  const activePattern = useSound((s) => s.activePattern);
  const loop = usePlayback((s) => s.loop);
  const loopOn = usePlayback((s) => s.loopOn);
  const [zoom, setZoom] = useState<number | null>(null);
  const [snap, setSnap] = useState<SnapMode>('beat');
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(800);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const isSong = !!doc?.tracks?.length || doc?.kind === 'song';
  const tempo = doc ? tempoOf(doc) : 120;
  const ts = doc ? timeSignatureOf(doc) : ([4, 4] as [number, number]);
  const spb = secondsPerBeat(tempo);
  const bpb = beatsPerBar(ts);
  const length = Math.max(doc ? docLengthSeconds(doc) : 1, audio ? audio.left.length / audio.rate : 0, isSong ? bpb * spb * 4 : 0.5);
  const fit = Math.max(4, (width - HEADER_W - 40) / (length * 1.1));
  const pps = zoom ?? fit; // pixels per second
  const contentW = Math.max(width - HEADER_W, length * 1.25 * pps + 80);
  const rows = useMemo(() => (doc ? rowsOf(doc) : []), [doc]);
  const lanesH = Math.max(rows.reduce((h, r) => h + r.h, 0), 60);

  const effSnap: SnapMode = isSong ? (['bar', 'beat', 'sixteenth', 'off'].includes(snap) ? snap : 'beat') : ['100ms', '10ms', 'off'].includes(snap) ? snap : '10ms';
  const snapSeconds = (t: number, mode: SnapMode = effSnap) => {
    const grid = mode === 'bar' ? bpb * spb : mode === 'beat' ? spb : mode === 'sixteenth' ? spb / 4 : mode === '100ms' ? 0.1 : mode === '10ms' ? 0.01 : 0;
    return Math.max(0, snapTo(t, grid));
  };

  const timeAt = (clientX: number) => {
    const el = scrollRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return Math.max(0, (clientX - r.left - HEADER_W + el.scrollLeft) / pps);
  };

  // Ctrl/Cmd + wheel zooms around the pointer.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left - HEADER_W;
      const t = (x + el.scrollLeft) / pps;
      const next = Math.min(20000, Math.max(2, pps * Math.exp(-e.deltaY * 0.0015)));
      setZoom(next);
      requestAnimationFrame(() => (el.scrollLeft = Math.max(0, t * next - x)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [pps]);

  // Window-level drag tracking: one undo step per drag, committed on release.
  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      let next: Drag = d;
      if (d.kind === 'clip') next = { ...d, dx: e.clientX - d.x0, dy: e.clientY - d.y0, copy: e.altKey };
      else if (d.kind === 'layer') next = { ...d, dx: e.clientX - d.x0, copy: e.altKey };
      else if (d.kind === 'clipResize' || d.kind === 'layerResize' || d.kind === 'line') next = { ...d, dx: e.clientX - d.x0 };
      else if (d.kind === 'loop') next = { ...d, t1: snapSeconds(timeAt(e.clientX)) };
      else if (d.kind === 'scrub') soundPlayer.seek(timeAt(e.clientX));
      dragRef.current = next;
      setDrag(next);
    };
    const up = () => {
      const d = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (d) finish(d);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  });

  function begin(d: Drag, e: ReactPointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = d;
    setDrag(d);
  }

  function finish(d: Drag) {
    const s = useSound.getState();
    const cur = s.doc;
    if (!cur) return;
    if (d.kind === 'clip') {
      const clip = cur.tracks?.[d.track]?.clips?.[d.clip];
      if (!clip) return;
      const startSec = barPosToBeats(clip.start, ts) * spb;
      const t = snapSeconds(startSec + d.dx / pps);
      const rowShift = Math.round(d.dy / TRACK_H);
      const toTrack = Math.max(0, Math.min((cur.tracks?.length ?? 1) - 1, d.track + rowShift));
      if (Math.abs(d.dx) < 3 && rowShift === 0) return;
      if (d.copy) {
        const r = addClip(cur, toTrack, { ...structuredClone(clip), start: beatsToBarPos(t / spb, ts) });
        s.update(r.doc, { kind: 'clip', track: toTrack, clip: r.index });
      } else {
        const r = moveClip(cur, d.track, d.clip, t / spb, toTrack);
        s.update(r.doc, { kind: 'clip', track: r.track, clip: r.index });
      }
    } else if (d.kind === 'clipResize') {
      const clip = cur.tracks?.[d.track]?.clips?.[d.clip];
      if (!clip || Math.abs(d.dx) < 3) return;
      const span = clipSpan(cur, clip);
      const plen = patternLength(cur.patterns?.[clip.pattern], ts);
      const beats = span.length + d.dx / pps / spb;
      s.update(resizeClip(cur, d.track, d.clip, Math.max(1, Math.round(beats / plen))));
    } else if (d.kind === 'layer') {
      const layer = cur.layers?.[d.index];
      if (!layer || Math.abs(d.dx) < 3) return;
      const start = Math.round(snapSeconds((layer.start ?? 0) + d.dx / pps) * 1e5) / 1e5;
      if (d.copy) {
        const r = addLayer(cur, { ...structuredClone(layer), start }, d.index + 1);
        s.update(r.doc, { kind: 'layer', index: r.index });
      } else s.update(updateLayer(cur, d.index, { start }), { kind: 'layer', index: d.index });
    } else if (d.kind === 'layerResize') {
      const layer = cur.layers?.[d.index];
      if (!layer || Math.abs(d.dx) < 3) return;
      const len = Math.max(0.01, layerLength(layer, cur) + d.dx / pps);
      const amp = scaleEnvelope(layer.amp, len);
      if (amp !== layer.amp) s.update(updateLayer(cur, d.index, { amp }));
    } else if (d.kind === 'line') {
      if (Math.abs(d.dx) < 3) return;
      const starts = voiceLineStarts(cur.voice);
      const start = Math.round(snapSeconds((starts[d.index] ?? 0) + d.dx / pps) * 1000) / 1000;
      s.update(updateVoiceLine(cur, d.index, { start }), { kind: 'voiceLine', index: d.index });
    } else if (d.kind === 'loop') {
      const a = Math.min(d.t0, d.t1);
      const b = Math.max(d.t0, d.t1);
      soundPlayer.setLoop(b - a > 0.01 ? { start: a, end: b } : null);
    }
  }

  if (!doc) return null;

  const ticks: Array<{ x: number; label: string | null; major: boolean }> = [];
  if (isSong) {
    const pxBeat = spb * pps;
    const totalBeats = contentW / pxBeat;
    for (let beat = 0; beat <= totalBeats; beat += 1) {
      const isBar = Math.abs(beat / bpb - Math.round(beat / bpb)) < 1e-6;
      if (!isBar && pxBeat < 10) continue;
      const bar = Math.round(beat / bpb);
      const showLabel = isBar && (bpb * pxBeat >= 28 || bar % Math.ceil(28 / (bpb * pxBeat)) === 0);
      ticks.push({ x: beat * pxBeat, label: showLabel ? String(bar + 1) : null, major: isBar });
    }
  } else {
    const step = tickStep(pps);
    for (let t = 0; t * pps <= contentW; t += step) ticks.push({ x: t * pps, label: fmtTick(t, step), major: true });
  }

  const loopX = loop ? { a: loop.start * pps, b: loop.end * pps } : null;
  const dragLoop = drag?.kind === 'loop' ? { a: Math.min(drag.t0, drag.t1) * pps, b: Math.max(drag.t0, drag.t1) * pps } : null;
  const shownLoop = dragLoop ?? loopX;
  const s = useSound.getState();
  const lineStarts = voiceLineStarts(doc.voice);

  return (
    <div className="snd-timeline">
      <div className="snd-view-tools">
        <span className="tool-caption muted">{isSong ? `${tempo} BPM · ${ts[0]}/${ts[1]}` : 'Seconds'}</span>
        <label className="snd-inline" title="Snap moves to this grid">
          <span className="muted">Snap</span>
          <select value={effSnap} onChange={(e) => setSnap(e.target.value as SnapMode)} aria-label="Snap">
            {(isSong ? ['bar', 'beat', 'sixteenth', 'off'] : ['100ms', '10ms', 'off']).map((m) => (
              <option key={m} value={m}>
                {m === 'sixteenth' ? '1/16' : m}
              </option>
            ))}
          </select>
        </label>
        <span className="bar-spacer" />
        <button type="button" className="tb-btn" title="Zoom out (Ctrl+wheel)" aria-label="Zoom out" onClick={() => setZoom(Math.max(2, pps / 1.5))}>
          −
        </button>
        <button type="button" className="tb-btn" title="Fit the whole sound" onClick={() => setZoom(null)}>
          Fit
        </button>
        <button type="button" className="tb-btn" title="Zoom in (Ctrl+wheel)" aria-label="Zoom in" onClick={() => setZoom(Math.min(20000, pps * 1.5))}>
          +
        </button>
      </div>
      <div className="snd-tl-scroll" ref={scrollRef}>
        <div className="snd-tl-content" style={{ width: HEADER_W + contentW, height: RULER_H + lanesH }}>
          {/* Ruler (sticky on top; the corner stays on the left) */}
          <div className="snd-ruler-row" style={{ width: HEADER_W + contentW, height: RULER_H }}>
            <div className="snd-ruler-corner" style={{ width: HEADER_W, height: RULER_H }}>
              <span className="muted">{isSong ? 'Bars' : 'Time'}</span>
            </div>
            <div
              className="snd-ruler"
              style={{ left: HEADER_W, width: contentW, height: RULER_H }}
              role="slider"
              aria-label="Playhead position (click to seek, Shift+drag to set the loop)"
              aria-valuemin={0}
              aria-valuemax={Math.round(length * 1000) / 1000}
              aria-valuenow={Math.round(usePlayback.getState().position * 1000) / 1000}
              tabIndex={0}
              onPointerDown={(e) => {
                const t = timeAt(e.clientX);
                if (e.shiftKey) begin({ kind: 'loop', t0: snapSeconds(t), t1: snapSeconds(t) }, e);
                else {
                  soundPlayer.seek(t);
                  begin({ kind: 'scrub' }, e);
                }
              }}
            >
              {ticks.map((t, i) => (
                <span key={i} className={`snd-tick${t.major ? ' is-major' : ''}`} style={{ left: t.x }}>
                  {t.label && <span className="snd-tick-label">{t.label}</span>}
                </span>
              ))}
              {shownLoop && (
                <span
                  className={`snd-loop-mark${loopOn || dragLoop ? ' is-on' : ''}`}
                  style={{ left: shownLoop.a, width: Math.max(2, shownLoop.b - shownLoop.a) }}
                  title="Loop region (click the loop button to toggle)"
                />
              )}
            </div>
          </div>
          {/* Grid + loop shading */}
          <div className="snd-tl-grid" style={{ left: HEADER_W, top: RULER_H, width: contentW, height: lanesH }}>
            {ticks.map((t, i) => (
              <span key={i} className={`snd-gridline${t.major ? ' is-major' : ''}`} style={{ left: t.x }} />
            ))}
            {shownLoop && (loopOn || dragLoop) && <span className="snd-loop-shade" style={{ left: shownLoop.a, width: shownLoop.b - shownLoop.a }} />}
          </div>

          {/* Rows */}
          {rows.map((row) => {
            const top = RULER_H + row.y;
            if (row.kind === 'track') {
              const track = doc.tracks![row.index]!;
              const key = `track:${row.index}`;
              const selected = isSel(selection, { kind: 'track', index: row.index });
              return (
                <div key={key} className="snd-tl-row" style={{ top, height: row.h, width: HEADER_W + contentW }}>
                  <div
                    className={`snd-row-head${selected ? ' is-selected' : ''}${track.mute ? ' is-muted' : ''}`}
                    style={{ width: HEADER_W }}
                    onClick={() => s.select({ kind: 'track', index: row.index })}
                  >
                    <Icon name="audio" size={12} className="muted" />
                    <span className="snd-row-name" title={track.name}>
                      {track.name || `Track ${row.index + 1}`}
                    </span>
                    <span className="snd-row-sub muted">{track.instrument ?? ''}</span>
                    <MuteSolo muted={!!track.mute} soloed={solo.includes(key)} onMute={() => s.toggleMute(key)} onSolo={() => s.toggleSolo(key)} label={track.name ?? 'track'} />
                  </div>
                  <div
                    className="snd-lane"
                    style={{ left: HEADER_W, width: contentW }}
                    onDoubleClick={(e) => {
                      const pattern = activePattern ?? Object.keys(doc.patterns ?? {})[0];
                      if (!pattern) return;
                      const beats = snapSeconds(timeAt(e.clientX), 'bar') / spb;
                      const r = addClip(doc, row.index, { start: beatsToBarPos(beats, ts), pattern });
                      s.update(r.doc, { kind: 'clip', track: row.index, clip: r.index });
                    }}
                    title="Double-click to add a clip of the open pattern"
                  >
                    {(track.clips ?? []).map((clip, ci) => {
                      const span = clipSpan(doc, clip);
                      let x = span.start * spb * pps;
                      let w = span.length * spb * pps;
                      let dy = 0;
                      const sel = isSel(selection, { kind: 'clip', track: row.index, clip: ci });
                      const dragging = drag && 'track' in drag && drag.track === row.index && drag.clip === ci ? drag : null;
                      if (dragging?.kind === 'clip') {
                        x = snapSeconds(span.start * spb + dragging.dx / pps) * pps;
                        dy = Math.round(dragging.dy / TRACK_H) * TRACK_H;
                      } else if (dragging?.kind === 'clipResize') {
                        const plen = patternLength(doc.patterns?.[clip.pattern], ts);
                        const reps = Math.max(1, Math.round((span.length + dragging.dx / pps / spb) / plen));
                        w = reps * plen * spb * pps;
                      }
                      const plen = patternLength(doc.patterns?.[clip.pattern], ts) * spb * pps;
                      const missing = !doc.patterns?.[clip.pattern];
                      return (
                        <div
                          key={ci}
                          className={`snd-clip${sel ? ' is-selected' : ''}${missing ? ' is-missing' : ''}${dragging?.kind === 'clip' && dragging.copy ? ' is-copy' : ''}`}
                          style={{ left: x, width: Math.max(6, w), transform: dy ? `translateY(${dy}px)` : undefined }}
                          role="button"
                          tabIndex={0}
                          aria-label={`Clip ${clip.pattern} at bar ${String(clip.start)}${clip.repeat ? `, ${clip.repeat} times` : ''}`}
                          aria-pressed={sel}
                          onPointerDown={(e) => {
                            if (e.button !== 0) return;
                            s.select({ kind: 'clip', track: row.index, clip: ci });
                            s.openPattern(clip.pattern, s.view === 'timeline' ? 'timeline' : undefined);
                            begin({ kind: 'clip', track: row.index, clip: ci, x0: e.clientX, y0: e.clientY, dx: 0, dy: 0, copy: e.altKey }, e);
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            s.openPattern(clip.pattern);
                          }}
                          title={`${clip.pattern}${clip.repeat ? ` ×${clip.repeat}` : ''} — drag to move, Alt+drag to copy, edge to repeat, double-click to edit`}
                        >
                          {plen > 8 &&
                            Array.from({ length: Math.max(0, Math.round(w / plen) - 1) }, (_, k) => (
                              <span key={k} className="snd-clip-rep" style={{ left: (k + 1) * plen }} />
                            ))}
                          <span className="snd-clip-label">
                            {clip.pattern}
                            {clip.repeat && clip.repeat > 1 ? <span className="muted"> ×{clip.repeat}</span> : null}
                          </span>
                          <span
                            className="snd-resize"
                            onPointerDown={(e) => {
                              if (e.button !== 0) return;
                              s.select({ kind: 'clip', track: row.index, clip: ci });
                              begin({ kind: 'clipResize', track: row.index, clip: ci, x0: e.clientX, dx: 0 }, e);
                            }}
                            aria-hidden="true"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            if (row.kind === 'layer') {
              const layer = doc.layers![row.index]!;
              const key = `layer:${row.index}`;
              const selected = isSel(selection, { kind: 'layer', index: row.index });
              const start = layer.start ?? 0;
              const len = layerLength(layer, doc);
              let x = start * pps;
              let w = len * pps;
              const dragging = drag && 'index' in drag && drag.index === row.index && (drag.kind === 'layer' || drag.kind === 'layerResize') ? drag : null;
              if (dragging?.kind === 'layer') x = snapSeconds(start + dragging.dx / pps) * pps;
              if (dragging?.kind === 'layerResize') w = Math.max(2, w + dragging.dx);
              const resizable = !!layer.amp && typeof layer.amp === 'object' && Array.isArray((layer.amp as { env?: unknown }).env);
              return (
                <div key={key} className="snd-tl-row" style={{ top, height: row.h, width: HEADER_W + contentW }}>
                  <div
                    className={`snd-row-head${selected ? ' is-selected' : ''}${layer.mute ? ' is-muted' : ''}`}
                    style={{ width: HEADER_W }}
                    onClick={() => s.select({ kind: 'layer', index: row.index })}
                  >
                    <Icon name="layers" size={12} className="muted" />
                    <span className="snd-row-name" title={layer.name}>
                      {layer.name || `layer ${row.index + 1}`}
                    </span>
                    <span className="snd-row-sub muted">{layer.source?.type ?? ''}</span>
                    <MuteSolo muted={!!layer.mute} soloed={solo.includes(key)} onMute={() => s.toggleMute(key)} onSolo={() => s.toggleSolo(key)} label={layer.name ?? 'layer'} />
                  </div>
                  <div className="snd-lane" style={{ left: HEADER_W, width: contentW }}>
                    <div
                      className={`snd-clip snd-layer-block${selected ? ' is-selected' : ''}${layer.mute ? ' is-muted' : ''}${dragging?.kind === 'layer' && dragging.copy ? ' is-copy' : ''}`}
                      style={{ left: x, width: Math.max(4, w) }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Layer ${layer.name ?? row.index + 1} at ${start.toFixed(3)} s`}
                      aria-pressed={selected}
                      onPointerDown={(e) => {
                        if (e.button !== 0) return;
                        s.select({ kind: 'layer', index: row.index });
                        begin({ kind: 'layer', index: row.index, x0: e.clientX, dx: 0, copy: e.altKey }, e);
                      }}
                      title={`${layer.name ?? ''} ${start.toFixed(3)}s → ${(start + len).toFixed(3)}s — drag to move, Alt+drag to copy${resizable ? ', edge to stretch the envelope' : ''}`}
                    >
                      <span className="snd-clip-label">{layer.name}</span>
                      {resizable && (
                        <span
                          className="snd-resize"
                          onPointerDown={(e) => {
                            if (e.button !== 0) return;
                            s.select({ kind: 'layer', index: row.index });
                            begin({ kind: 'layerResize', index: row.index, x0: e.clientX, dx: 0 }, e);
                          }}
                          aria-hidden="true"
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            // Voice lines
            const voiceSelected = selection?.kind === 'voice';
            return (
              <div key="voice" className="snd-tl-row" style={{ top, height: row.h, width: HEADER_W + contentW }}>
                <div className={`snd-row-head${voiceSelected ? ' is-selected' : ''}`} style={{ width: HEADER_W }} onClick={() => s.select({ kind: 'voice' })}>
                  <Icon name="send" size={12} className="muted" />
                  <span className="snd-row-name">Voice</span>
                  <span className="snd-row-sub muted">{doc.voice?.voice ?? ''}</span>
                  <MuteSolo muted={false} soloed={solo.includes('voice')} onSolo={() => s.toggleSolo('voice')} label="voice" />
                </div>
                <div
                  className="snd-lane"
                  style={{ left: HEADER_W, width: contentW }}
                  onDoubleClick={(e) => {
                    const r = addVoiceLine(doc, { text: 'New line.', start: Math.round(snapSeconds(timeAt(e.clientX)) * 1000) / 1000 });
                    s.update(r.doc, { kind: 'voiceLine', index: r.index });
                  }}
                  title="Double-click to add a line"
                >
                  {(doc.voice?.lines ?? []).map((line, li) => {
                    const sel = isSel(selection, { kind: 'voiceLine', index: li });
                    let x = (lineStarts[li] ?? 0) * pps;
                    if (drag?.kind === 'line' && drag.index === li) x = snapSeconds((lineStarts[li] ?? 0) + drag.dx / pps) * pps;
                    return (
                      <div
                        key={li}
                        className={`snd-clip snd-line-block${sel ? ' is-selected' : ''}`}
                        style={{ left: x, width: Math.max(8, voiceLineLength(line) * pps) }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Voice line: ${line.text}`}
                        aria-pressed={sel}
                        onPointerDown={(e) => {
                          if (e.button !== 0) return;
                          s.select({ kind: 'voiceLine', index: li });
                          begin({ kind: 'line', index: li, x0: e.clientX, dx: 0 }, e);
                        }}
                        title={`${line.text} — drag to move (length is estimated until rendered)`}
                      >
                        <span className="snd-clip-label">“{line.text}”</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <div className="snd-tl-empty muted" style={{ top: RULER_H + 12, left: HEADER_W + 12 }}>
              Nothing on the timeline yet. Add a layer, track or voice line from the outline, or describe the sound to the AI.
            </div>
          )}
          <Playhead pps={pps} height={RULER_H + lanesH} />
        </div>
      </div>
    </div>
  );
}

function Playhead({ pps, height }: { pps: number; height: number }) {
  const position = usePlayback((s) => s.position);
  return <span className="snd-tl-playhead" style={{ left: HEADER_W + position * pps, height }} aria-hidden="true" />;
}

export function MuteSolo({
  muted,
  soloed,
  onMute,
  onSolo,
  label,
}: {
  muted: boolean;
  soloed: boolean;
  onMute?: () => void;
  onSolo?: () => void;
  label: string;
}) {
  return (
    <span className="snd-ms" onClick={(e) => e.stopPropagation()}>
      {onMute && (
        <button type="button" className={`snd-ms-btn${muted ? ' is-mute' : ''}`} aria-pressed={muted} aria-label={`Mute ${label}`} title="Mute (saved in the document)" onClick={onMute}>
          M
        </button>
      )}
      {onSolo && (
        <button type="button" className={`snd-ms-btn${soloed ? ' is-solo' : ''}`} aria-pressed={soloed} aria-label={`Solo ${label}`} title="Solo (editor only; re-renders)" onClick={onSolo}>
          S
        </button>
      )}
    </span>
  );
}

